(() => {
"use strict";
if (window.__ardmoreHeroNativeLoaded) return;
window.__ardmoreHeroNativeLoaded = true;
const instances = new Map();
const enhance = root => {
    if (instances.has(root)) return;
    const configEl = root.querySelector('[data-ardmore-hero-config]');
    if (!configEl) return;
    let config; try {config = JSON.parse(configEl.textContent);} catch(error) {console.warn('Ardmore hero config is invalid',error);return;}
    const controller = new AbortController(); instances.set(root,controller);
    let productInfoEl = null;
/*
 * Product Page Hero Redesign
 * Puts quantity and Add to cart on one row, adds Secure Payments and shipping cards beneath it, adds an open
 * Product Description accordion above the existing accordions, and upgrades the gallery: hover arrows on desktop,
 * dot pagination on mobile, and a thumbnail rail with click-to-zoom in the fullscreen media modal.
 */

const testId = "ARD-004";

// Scopes the stylesheet (.ARD-004_var_1).
root.classList.add(`${testId}_var_1`);
root.classList.toggle(`${testId}_hide-tax`, !!config.hideTax);

const SELECTORS = {
	productInfo: ".product__info-container",
	quantityForm: '[id^="Quantity-Form-"]',
	productForm: "product-form.product-form",
	accordion: "div.product__accordion",
	galleryViewer: '[id^="GalleryViewer-"]',
	galleryThumbnail: '[id^="GalleryThumbnails-"] button.thumbnail',
	mediaSlide: ".product__media-item",
	slider: '[id^="Slider-"]',
	sliderCurrent: ".slider-counter--current",
	pageSection: "section.shopify-section",
	mediaModal: "product-modal",
	modalDialog: ".product-media-modal__dialog",
	modalContent: ".product-media-modal__content",
};

const ZOOM_SCALE = 2.5;
const DRAG_THRESHOLD = 6;

const isDesktop = window.matchMedia("(min-width: 750px)");

const INFO_ICON_SVG = `<svg class="icon icon-accordion" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20"><circle cx="10" cy="10" r="9" stroke="currentColor" stroke-width="1.2" fill="none"/><path d="M10 9V14" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><circle cx="10" cy="6.25" r="0.9" fill="currentColor"/></svg>`;

// Rotated 90deg per side in CSS. The viewBox is nudged so the caret's own centre sits on the button centre.
const GALLERY_ARROW_SVG = `<svg viewBox="-0.06 -0.255 12 8" width="12" height="8" fill="none" aria-hidden="true" focusable="false"><path d="M10.72 1.16a.55.55 0 0 0-.78 0L5.94 5.17 1.94 1.16a.55.55 0 0 0-.78.78l4.39 4.39a.55.55 0 0 0 .78 0l4.39-4.39a.55.55 0 0 0 0-.78Z" fill="currentColor"/></svg>`;

const escapeHtml = value => String(value || "").replace(/[&<>"']/g, c => ({"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;"}[c]));
/**
 * Index of the slide in view. Dawn's counter follows free scrolling where `is-active` does not, so it is read first.
 */
const getActiveSlideIndex = (galleryViewerEl) => {
	const counterEl = galleryViewerEl.querySelector(SELECTORS.sliderCurrent);
	if (counterEl) {
		const page = parseInt(counterEl.textContent, 10);
		if (!Number.isNaN(page) && page > 0) return page - 1;
	}

	const slides = [...galleryViewerEl.querySelectorAll(SELECTORS.mediaSlide)];
	return Math.max(0, slides.findIndex((slide) => slide.classList.contains("is-active")));
};

/**
 * Moves the gallery to a slide through Dawn's media-gallery, falling back to scrolling the slider.
 */
const goToGallerySlide = (galleryViewerEl, index) => {
	const slide = galleryViewerEl.querySelectorAll(SELECTORS.mediaSlide)[index];
	if (!slide) return;

	const mediaGalleryEl = galleryViewerEl.closest("media-gallery");

	if (mediaGalleryEl?.setActiveMedia && slide.dataset.mediaId) {
		mediaGalleryEl.setActiveMedia(slide.dataset.mediaId, false);
		return;
	}

	galleryViewerEl.querySelector(SELECTORS.slider)?.scrollTo({ left: slide.offsetLeft, behavior: "smooth" });
};

/**
 * Short numeric media id shared by a gallery slide (`...__main-<id>`) and its modal image (`<id>`).
 */
const shortMediaId = (mediaId) => (mediaId || "").split("-").pop();

const isThumbnailVisible = (thumbEl) => {
	const itemEl = thumbEl.closest("li");
	return !itemEl || getComputedStyle(itemEl).display !== "none";
};

/**
 * Media ids of the selected variant. Dawn hides other colours' media through the thumbnail rail, and the thumbnails
 * are index-aligned to the slides, so the visible thumbnails decide. If the counts differ, every slide counts.
 */
const getVariantMediaIds = (galleryViewerEl) => {
	const gallery = galleryViewerEl.closest("media-gallery") || galleryViewerEl;
	const thumbnails = [...gallery.querySelectorAll(SELECTORS.galleryThumbnail)];
	const slides = [...galleryViewerEl.querySelectorAll(SELECTORS.mediaSlide)];

	if (!thumbnails.length || thumbnails.length !== slides.length) {
		return new Set(slides.map((slide) => shortMediaId(slide.dataset.mediaId)));
	}

	return new Set(slides.filter((_, index) => isThumbnailVisible(thumbnails[index])).map((slide) => shortMediaId(slide.dataset.mediaId)));
};

/**
 * Adds prev/next arrows over the main image (desktop only, hidden on mobile in CSS).
 * Each arrow clicks the adjacent visible thumbnail, so Dawn's own handler swaps the image and keeps its state.
 * Hidden thumbnails belong to other colours and are skipped, so the image never jumps to another colour.
 */
const setupGalleryArrows = (galleryViewerEl) => {
	if (galleryViewerEl.querySelector(`.${testId}_gallery-arrow`)) return;
	const gallery = galleryViewerEl.closest("media-gallery") || galleryViewerEl;
	if (galleryViewerEl.querySelectorAll(SELECTORS.mediaSlide).length <= 1) return;

	const buildArrow = (direction) => {
		const arrowEl = document.createElement("button");
		arrowEl.type = "button";
		arrowEl.className = `${testId}_gallery-arrow ${testId}_gallery-arrow--${direction}`;
		arrowEl.setAttribute("aria-label", direction === "prev" ? "Previous image" : "Next image");
		arrowEl.innerHTML = GALLERY_ARROW_SVG;

		arrowEl.addEventListener("click", (e) => {
			e.preventDefault();
			e.stopPropagation();

			const thumbnails = [...gallery.querySelectorAll(SELECTORS.galleryThumbnail)].filter(isThumbnailVisible);
			const total = thumbnails.length;
			if (!total) { const slides = [...galleryViewerEl.querySelectorAll(SELECTORS.mediaSlide)].filter(slide => getComputedStyle(slide).display !== "none"); const current = Math.max(0, slides.findIndex(slide => slide.classList.contains("is-active"))); const next = (current + (direction === "prev" ? -1 : 1) + slides.length) % slides.length; if (slides[next]) goToGallerySlide(galleryViewerEl, [...galleryViewerEl.querySelectorAll(SELECTORS.mediaSlide)].indexOf(slides[next])); return; }
			const current = Math.max(
				0,
				thumbnails.findIndex((thumbEl) => thumbEl.getAttribute("aria-current") === "true")
			);
			const next = direction === "prev" ? (current - 1 + total) % total : (current + 1) % total;

			thumbnails[next].click();
		});

		return arrowEl;
	};

	galleryViewerEl.append(buildArrow("prev"), buildArrow("next"));
};

/**
 * Marks the dot for the slide in view. The counter's position among the visible slides maps straight onto the dots.
 */
const syncGalleryDots = (galleryViewerEl) => {
    const slides = [...galleryViewerEl.querySelectorAll(SELECTORS.mediaSlide)].filter(slide => getComputedStyle(slide).display !== "none");
    const current = isDesktop.matches ? (slides.find(slide => slide.classList.contains('is-active')) || slides[getActiveSlideIndex(galleryViewerEl)]) : (slides[getActiveSlideIndex(galleryViewerEl)] || slides.find(slide => slide.classList.contains('is-active')));
    galleryViewerEl.querySelectorAll(`.${testId}_gallery-dot`).forEach(dot => {
        const active = dot.dataset.mediaId === shortMediaId(current?.dataset.mediaId);
        dot.classList.toggle(`${testId}_gallery-dot--active`, active);
        dot.setAttribute('aria-current', String(active));
    });
};

/**
 * Builds one dot per image of the selected variant. Call again after a variant change:
 * Dawn reorders the slides and hides other colours' media when the colour changes.
 */
const renderGalleryDots = (galleryViewerEl) => {
	const dotsWrapEl = galleryViewerEl.querySelector(`.${testId}_gallery-dots`);
	if (!dotsWrapEl) return;

	const variantIds = getVariantMediaIds(galleryViewerEl);
	const variantSlides = [...galleryViewerEl.querySelectorAll(SELECTORS.mediaSlide)].filter((slide) => variantIds.has(shortMediaId(slide.dataset.mediaId)));

	dotsWrapEl.textContent = "";

	variantSlides.forEach((slide, index) => {
		const dotEl = document.createElement("button");
		dotEl.type = "button";
		dotEl.className = `${testId}_gallery-dot`;
		dotEl.dataset.mediaId = shortMediaId(slide.dataset.mediaId);
		dotEl.setAttribute("aria-label", `View image ${index + 1} of ${variantSlides.length}`);
		dotEl.addEventListener("click", () => goToGallerySlide(galleryViewerEl, [...galleryViewerEl.querySelectorAll(SELECTORS.mediaSlide)].indexOf(slide)));
		dotsWrapEl.appendChild(dotEl);
	});

	// A single-image variant has nothing to page between.
	dotsWrapEl.classList.toggle(`${testId}_gallery-dots--empty`, variantSlides.length <= 1);
	syncGalleryDots(galleryViewerEl);
};

/**
 * Adds the mobile dot pagination (hidden on desktop in CSS) and keeps it in step with swipes.
 */
const setupGalleryDots = (galleryViewerEl) => {
	if (galleryViewerEl.querySelectorAll(SELECTORS.mediaSlide).length <= 1 || galleryViewerEl.querySelector(`.${testId}_gallery-dots`)) return;

	const dotsWrapEl = document.createElement("div");
	dotsWrapEl.className = `${testId}_gallery-dots`;
	dotsWrapEl.setAttribute("role", "group");
	dotsWrapEl.setAttribute("aria-label", "Product image gallery");
	galleryViewerEl.appendChild(dotsWrapEl);

	galleryViewerEl.addEventListener("slideChanged", () => syncGalleryDots(galleryViewerEl));
	galleryViewerEl.querySelector(SELECTORS.slider)?.addEventListener("scroll", () => syncGalleryDots(galleryViewerEl), { passive: true });

	renderGalleryDots(galleryViewerEl);
};

/**
 * Adds a thumbnail rail to Dawn's fullscreen media modal on both breakpoints; a thumb swaps the shown image via
 * Dawn's `.active` class. On desktop the modal also becomes a single fit-to-screen image with click-to-zoom and
 * drag-to-pan. Dawn's modal closes on any mouse pointerup, so the rail and the image stop that event bubbling.
 * Single-image products keep Dawn's default modal.
 */
const setupMediaModal = (modalEl) => {
	const contentEl = modalEl.querySelector(SELECTORS.modalContent);
	if (!contentEl) return;

	const allMediaEls = [...contentEl.querySelectorAll(":scope > [data-media-id]")];
	if (allMediaEls.length <= 1 || allMediaEls.some(el => el.tagName !== "IMG") || modalEl.classList.contains(`${testId}_modal-enhanced`)) return;
	modalEl.classList.add(`${testId}_var_1`);

	// The browser's native image drag would fight the pan gesture.
	allMediaEls.forEach(el => {
        el.draggable = false; el.tabIndex = 0; el.setAttribute('role','button'); el.setAttribute('aria-label','Zoom product image');
        el.addEventListener('keydown', e => { if (!isDesktop.matches || !['Enter',' '].includes(e.key)) return; e.preventDefault(); if (zoomScale > 1) resetZoom(); else { const rect = el.getBoundingClientRect(); zoomInAt(el,rect.left+rect.width/2,rect.top+rect.height/2); } });
    });

	let mediaEls = [];
	let thumbBtns = [];
	let zoomScale = 1;
	let panX = 0;
	let panY = 0;
	let fitW = 0;
	let fitH = 0;
	let dragging = false;
	let dragMoved = false;
	let startX = 0;
	let startY = 0;
	let startPanX = 0;
	let startPanY = 0;

	const thumbSrc = (el) => {
		const imgEl = el.tagName === "IMG" ? el : el.querySelector("img");
		return (imgEl?.getAttribute("src") || "").replace(/([?&]width=)\d+/, "$1160");
	};

	const activeImgEl = () => contentEl.querySelector(":scope > img.active");

	const applyTransform = (imgEl) => {
		imgEl.style.transform = zoomScale === 1 ? "" : `translate(${panX}px, ${panY}px) scale(${zoomScale})`;
	};

	// Keeps the scaled image covering the viewport so panning never reveals empty gutters.
	const clampPan = () => {
		const maxX = Math.max(0, (fitW * zoomScale - window.innerWidth) / 2);
		const maxY = Math.max(0, (fitH * zoomScale - window.innerHeight) / 2);
		panX = Math.min(maxX, Math.max(-maxX, panX));
		panY = Math.min(maxY, Math.max(-maxY, panY));
	};

	const resetZoom = () => {
		zoomScale = 1;
		panX = 0;
		panY = 0;
		dragging = false;
		allMediaEls.forEach((el) => {
			if (el.tagName === "IMG") el.style.transform = "";
		});
		contentEl.classList.remove(`${testId}_modal-content--zoomed`, `${testId}_modal-content--grabbing`);
	};

	// Zooms toward the click so the detail under the cursor fills the view.
	const zoomInAt = (imgEl, clientX, clientY) => {
		const rect = imgEl.getBoundingClientRect();
		fitW = rect.width;
		fitH = rect.height;
		zoomScale = ZOOM_SCALE;
		panX = -(clientX - (rect.left + rect.width / 2)) * (zoomScale - 1);
		panY = -(clientY - (rect.top + rect.height / 2)) * (zoomScale - 1);
		clampPan();
		contentEl.classList.add(`${testId}_modal-content--zoomed`);
		applyTransform(imgEl);
	};

	const highlightThumb = (index) => {
		thumbBtns.forEach((btn, i) => {
			const isActive = i === index;
			btn.classList.toggle(`${testId}_modal-thumb--active`, isActive);
			btn.setAttribute("aria-current", isActive ? "true" : "false");
		});
	};

	// Clears `.active` across every media, not just the current set, so exactly one image shows.
	// Mobile keeps Dawn's scrollable full-size image, so the scroll resets to the top of the new image.
	const selectImage = (index) => {
		if (isDesktop.matches) resetZoom();
		allMediaEls.forEach((el) => el.classList.remove("active"));
		if (!mediaEls[index]) return;
		mediaEls[index].classList.add("active");
		if (!isDesktop.matches) contentEl.scrollTop = 0;
		highlightThumb(index);
	};

	// Starts the fullscreen view on the media Dawn marked `.active` for the opened image, falling back to the first.
	const syncToActive = () => {
		selectImage(
			Math.max(
				0,
				mediaEls.findIndex((el) => el.classList.contains("active"))
			)
		);
	};

	const thumbsEl = document.createElement("div");
	thumbsEl.className = `${testId}_modal-thumbs`;
	thumbsEl.setAttribute("role", "group");
	thumbsEl.setAttribute("aria-label", "Product images");

	// Rebuilt on every open because Dawn reorders the modal media per variant.
	const buildThumbs = () => {
		mediaEls = [...contentEl.querySelectorAll(":scope > [data-media-id]")];

		thumbsEl.textContent = "";
		thumbBtns = mediaEls.map((el, index) => {
			const btn = document.createElement("button");
			btn.type = "button";
			btn.className = `${testId}_modal-thumb`;
			
			btn.setAttribute("aria-label", `View image ${index + 1} of ${mediaEls.length}`);
			btn.innerHTML = `<img src="${thumbSrc(el)}" alt="" loading="lazy">`;
			btn.addEventListener("click", () => selectImage(index));
			thumbsEl.appendChild(btn);
			return btn;
		});
	};

	// Dawn closes on any mouse pointerup; stopping it under the rail lets a tap switch image instead of closing.
	thumbsEl.addEventListener("pointerup", (e) => e.stopPropagation());

	(modalEl.querySelector(SELECTORS.modalDialog) || modalEl).appendChild(thumbsEl);
	modalEl.classList.add(`${testId}_modal-enhanced`);
	buildThumbs();

	contentEl.addEventListener("pointerdown", (e) => {
		// Only a zoomed image pans; a click on the fit image zooms in on pointerup.
		if (!isDesktop.matches || zoomScale === 1 || !e.target.closest("img")) return;

		dragging = true;
		dragMoved = false;
		startX = e.clientX;
		startY = e.clientY;
		startPanX = panX;
		startPanY = panY;
		contentEl.classList.add(`${testId}_modal-content--grabbing`);
		e.target.setPointerCapture?.(e.pointerId);
		e.preventDefault();
	});

	contentEl.addEventListener("pointermove", (e) => {
		if (!dragging) return;

		panX = startPanX + (e.clientX - startX);
		panY = startPanY + (e.clientY - startY);
		if (Math.abs(e.clientX - startX) > DRAG_THRESHOLD || Math.abs(e.clientY - startY) > DRAG_THRESHOLD) dragMoved = true;
		clampPan();
		const active = activeImgEl();
		if (active) applyTransform(active);
	});

	contentEl.addEventListener("pointerup", (e) => {
		if (!isDesktop.matches) return;

		// A click on the empty stage still reaches Dawn and closes the modal; only the image is intercepted.
		const imgEl = e.target.closest("img");
		if (!imgEl) return;
		e.stopPropagation();

		if (dragging) {
			dragging = false;
			contentEl.classList.remove(`${testId}_modal-content--grabbing`);
			imgEl.releasePointerCapture?.(e.pointerId);
			// A pan, not a click: leave the zoom as it is.
			if (dragMoved) return;
		}

		if (zoomScale > 1) resetZoom();
		else zoomInAt(imgEl, e.clientX, e.clientY);
	});

	// Dawn's modal-opener opens the modal and marks the opened media `.active` in its own click handler on the button,
	// which runs before this bubbles to document. Rebuild the rail for the current variant and start on that image.
	document.addEventListener("click", (e) => {
		const openerEl = e.target.closest("modal-opener[data-modal]");
		if (!openerEl || document.querySelector(openerEl.dataset.modal) !== modalEl) return;

		buildThumbs();
		syncToActive();
	}, {signal: controller.signal});
};

/**
 * Applies every change to the product page.
 */
const enhanceGallery = galleryViewerEl => {
    setupGalleryArrows(galleryViewerEl); setupGalleryDots(galleryViewerEl);
    const gallery = galleryViewerEl.closest('media-gallery') || galleryViewerEl;
    const signature = [...galleryViewerEl.querySelectorAll(SELECTORS.mediaSlide)].map(slide => slide.dataset.mediaId + ':' + getComputedStyle(slide).display).join('|') + [...gallery.querySelectorAll(SELECTORS.galleryThumbnail)].map(thumb => thumb.getAttribute('aria-current') + ':' + isThumbnailVisible(thumb)).join('|');
    if (galleryViewerEl.dataset.ardmoreMediaSignature !== signature) {
        galleryViewerEl.dataset.ardmoreMediaSignature = signature;
        renderGalleryDots(galleryViewerEl);
    }
    syncGalleryDots(galleryViewerEl);
};
const refresh = () => {
    if (!root.isConnected) return;
    root.querySelectorAll(SELECTORS.galleryViewer).forEach(enhanceGallery);
    const modal = root.querySelector(SELECTORS.mediaModal) || document.getElementById(`ProductModal-${config.sectionId}`);
    if (modal) setupMediaModal(modal);
};
refresh();
let queued = false;
const observer = new MutationObserver(records => {
    if (!records.some(record => !record.target.closest?.(`.${testId}_gallery-dots, .${testId}_modal-thumbs`) && (record.type === 'childList' || record.target.matches?.('.product__media-item, .thumbnail, [id^="GalleryThumbnails-"] li, .slider-counter--current')))) return;
    if (queued) return; queued = true;
    requestAnimationFrame(() => {queued = false; refresh();});
});
observer.observe(root,{childList:true,subtree:true,attributes:true,attributeFilter:['class','style','hidden','aria-current']});
controller.signal.addEventListener('abort',()=>observer.disconnect(),{once:true});

};
const scan = () => {
    for (const [root, controller] of instances) if (!root.isConnected) {controller.abort();instances.delete(root);}
    document.querySelectorAll('[data-ardmore-hero-native]').forEach(enhance);
    document.documentElement.classList.toggle('ARD-004_hide-utility',[...instances.keys()].some(root => JSON.parse(root.querySelector('[data-ardmore-hero-config]').textContent).hideUtility));
};
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',scan,{once:true});else scan();
document.addEventListener('shopify:section:load',scan);
document.addEventListener('shopify:section:unload',e => {
    for (const [root,controller] of instances) if (e.target.contains(root)) {controller.abort();instances.delete(root);}
    document.documentElement.classList.toggle('ARD-004_hide-utility',[...instances.keys()].some(root => JSON.parse(root.querySelector('[data-ardmore-hero-config]').textContent).hideUtility));
});
})();
